// Minimal JMAP client for Stalwart (RFC 8620 / 8621) - original F0 client plus:
//   - send(): cc/bcc, attachments (JMAP blob upload), keywords
//   - downloadUrl(): blob download link for attachments
//   - setKeywords()/move(): used by the mailbox actions
const CORE = 'urn:ietf:params:jmap:core';
const MAIL = 'urn:ietf:params:jmap:mail';
const SUBMISSION = 'urn:ietf:params:jmap:submission';

export class JmapClient {
  constructor({ baseUrl = '', authHeader = null } = {}) { this.baseUrl = baseUrl; this.authHeader = authHeader; this.session = null; }
  static basicAuth(email, password) { return 'Basic ' + btoa(`${email}:${password}`); }

  async connect() {
    const res = await fetch(`${this.baseUrl}/jmap/session`, { headers: { Authorization: this.authHeader } });
    if (!res.ok) throw new JmapError(`session failed: ${res.status}`, res.status);
    this.session = await res.json();
    this.accountId = this.session.primaryAccounts?.[MAIL] || Object.keys(this.session.accounts || {})[0];
    return this.session;
  }

  async request(methodCalls, using = [CORE, MAIL]) {
    const res = await fetch(`${this.baseUrl}/jmap`, { method: 'POST', headers: { Authorization: this.authHeader, 'Content-Type': 'application/json' }, body: JSON.stringify({ using, methodCalls }) });
    if (!res.ok) throw new JmapError(`request failed: ${res.status}`, res.status);
    const body = await res.json();
    const err = (body.methodResponses || []).find((m) => m[0] === 'error');
    if (err) throw new JmapError(`${err[1].type}: ${err[1].description || ''}`, 400);
    return body.methodResponses;
  }

  async listMailboxes() { const [r] = await this.request([['Mailbox/get', { accountId: this.accountId, ids: null }, '0']]); return r[1].list; }

  async listEmails(mailboxId, limit = 50) {
    const r = await this.request([
      ['Email/query', { accountId: this.accountId, filter: { inMailbox: mailboxId }, sort: [{ property: 'receivedAt', isAscending: false }], limit }, '0'],
      ['Email/get', { accountId: this.accountId, '#ids': { resultOf: '0', name: 'Email/query', path: '/ids' }, properties: ['id', 'subject', 'from', 'to', 'receivedAt', 'preview', 'hasAttachment', 'keywords'] }, '1'],
    ]);
    return r[1][1].list;
  }

  async getEmailBody(emailId) {
    const [r] = await this.request([['Email/get', { accountId: this.accountId, ids: [emailId], properties: ['id', 'subject', 'from', 'to', 'cc', 'bcc', 'receivedAt', 'bodyValues', 'textBody', 'htmlBody', 'attachments', 'keywords'], fetchAllBodyValues: true }, '0']]);
    return r[1].list[0];
  }

  // Save (or replace) a draft in the Drafts mailbox WITHOUT submitting. Pass an
  // existing draftId to replace it (keeps the Drafts folder tidy). Returns the id.
  async saveDraft({ draftId, from, to, cc, bcc, subject, textBody, attachments = [] }) {
    const roles = await this.mailboxIdsByRole();
    const draftsId = roles.drafts || roles.inbox;
    const list = (v) => (Array.isArray(v) ? v : String(v || '').split(',')).map((s) => s.trim()).filter(Boolean).map((email) => ({ email }));
    const blobs = [];
    for (const f of attachments) { const b = await this.upload(f); blobs.push({ blobId: b.blobId, type: b.type, name: f.name, disposition: 'attachment' }); }
    const bodyStructure = blobs.length ? { type: 'multipart/mixed', subParts: [{ type: 'text/plain', partId: 'b1' }, ...blobs] } : { type: 'text/plain', partId: 'b1' };
    const ccL = list(cc), bccL = list(bcc);
    const create = { d: { mailboxIds: { [draftsId]: true }, keywords: { $draft: true, $seen: true }, from: [{ email: from }], to: list(to), cc: ccL.length ? ccL : undefined, bcc: bccL.length ? bccL : undefined, subject: subject || '', bodyStructure, bodyValues: { b1: { value: textBody || '' } } } };
    const [r] = await this.request([['Email/set', { accountId: this.accountId, ...(draftId ? { destroy: [draftId] } : {}), create }, '0']], [CORE, MAIL]);
    return r[1].created?.d?.id || null;
  }

  async deleteEmail(id) { return this.request([['Email/set', { accountId: this.accountId, destroy: [id] }, '0']]); }

  async setKeywords(emailId, patch) {
    const update = Object.fromEntries(Object.entries(patch).map(([k, v]) => [`keywords/${k}`, v]));
    return this.request([['Email/set', { accountId: this.accountId, update: { [emailId]: update } }, '0']]);
  }
  async move(emailId, mailboxId) { return this.request([['Email/set', { accountId: this.accountId, update: { [emailId]: { mailboxIds: { [mailboxId]: true } } } }, '0']]); }

  async mailboxIdsByRole() { const byRole = {}; for (const b of await this.listMailboxes()) if (b.role) byRole[b.role] = b.id; return byRole; }

  async getIdentityId(email) {
    const [r] = await this.request([['Identity/get', { accountId: this.accountId }, '0']], [CORE, SUBMISSION]);
    const list = r[1].list || []; return (list.find((i) => i.email === email) || list[0])?.id;
  }

  /** Upload a File/Blob; returns { blobId, type, size }. */
  async upload(file) {
    const url = (this.session?.uploadUrl || `${this.baseUrl}/jmap/upload/{accountId}/`).replace('{accountId}', this.accountId).replace(/^https?:\/\/[^/]+/, this.baseUrl);
    const res = await fetch(url, { method: 'POST', headers: { Authorization: this.authHeader, 'Content-Type': file.type || 'application/octet-stream' }, body: file });
    if (!res.ok) throw new JmapError(`upload failed: ${res.status}`, res.status);
    return res.json();
  }

  /** Same-origin download URL for an attachment part ({ blobId, name, type }). */
  downloadUrl(a) {
    const tpl = this.session?.downloadUrl || `${this.baseUrl}/jmap/download/{accountId}/{blobId}/{name}?accept={type}`;
    return tpl.replace(/^https?:\/\/[^/]+/, this.baseUrl).replace('{accountId}', this.accountId).replace('{blobId}', a.blobId).replace('{name}', encodeURIComponent(a.name || 'file')).replace('{type}', encodeURIComponent(a.type || 'application/octet-stream'));
  }

  /**
   * Send: Email/set (draft) -> EmailSubmission/set, moving to Sent on success.
   * @param {{from, to, cc?, bcc?, subject, textBody, attachments?: File[], keywords?: object}} msg
   */
  async send({ from, to, cc, bcc, subject, textBody, attachments = [], keywords = {} }) {
    const roles = await this.mailboxIdsByRole();
    const draftsId = roles.drafts || roles.inbox, sentId = roles.sent || null;
    const identityId = await this.getIdentityId(from);
    const list = (v) => (Array.isArray(v) ? v : String(v || '').split(',')).map((s) => s.trim()).filter(Boolean).map((email) => ({ email }));
    const toList = list(to), ccList = list(cc), bccList = list(bcc);
    const blobs = [];
    for (const f of attachments) { const b = await this.upload(f); blobs.push({ blobId: b.blobId, type: b.type, name: f.name, disposition: 'attachment' }); }
    const bodyStructure = blobs.length ? { type: 'multipart/mixed', subParts: [{ type: 'text/plain', partId: 'b1' }, ...blobs] } : { type: 'text/plain', partId: 'b1' };
    const r = await this.request([
      ['Email/set', { accountId: this.accountId, create: { draft: { mailboxIds: { [draftsId]: true }, keywords: { $draft: true, $seen: true, ...keywords }, from: [{ email: from }], to: toList, cc: ccList.length ? ccList : undefined, bcc: bccList.length ? bccList : undefined, subject: subject || '', bodyStructure, bodyValues: { b1: { value: textBody || '' } } } } }, '0'],
      ['EmailSubmission/set', { accountId: this.accountId, onSuccessUpdateEmail: sentId ? { '#sendIt': { mailboxIds: { [sentId]: true }, 'keywords/$draft': null } } : undefined, create: { sendIt: { emailId: '#draft', identityId, envelope: { mailFrom: { email: from }, rcptTo: [...toList, ...ccList, ...bccList].map((x) => ({ email: x.email })) } } } }, '1'],
    ], [CORE, MAIL, SUBMISSION]);
    const sub = r.find((x) => x[0] === 'EmailSubmission/set');
    if (sub?.[1]?.notCreated?.sendIt) throw new JmapError('send failed: ' + JSON.stringify(sub[1].notCreated.sendIt));
    return sub?.[1]?.created?.sendIt;
  }
}

export class JmapError extends Error { constructor(msg, status) { super(msg); this.name = 'JmapError'; this.status = status; } }
